import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Drawer from '~/components/Drawer'
import { MilkTestType } from '../MilkTestCard'
import dayjs from 'dayjs'
import { DrawerFooter, StyledErrorLabel } from '../../../MilkDelivery/ShowTours/style'
import { GlobalEl } from '~/styles/app'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { Form } from 'semantic-ui-react'
import { getError } from '~/components/FormFields/util'
import { DateInput } from 'semantic-ui-calendar-react'
import { useAuth } from '~/routes/components/AuthProvider'
import { parseDate } from '~/utils/helpers'

interface Props {
  isVisible: boolean
  milkTestData?: MilkTestType
  handleHiddenDrawer: () => void
  handleSubmitMilkTest: (data: MilkTestType, id?: string) => void
  inhibitorOptions: any[]
  initialDate: string
}

const defaultMilkTestData = {
  date: '',
  fat: '0.00',
  protein: '0.00',
  lactose: '0.00',
  cellCount: 0,
  germCount: 0,
  freezingPoint: null,
  inhibitor: null,
  urea: 0,
  fatFreeDryMatter: '0.00',
  freeFattyAcid: '0.00',
  inhibitorLabel: ''
} as MilkTestType

const MilkTestEditDrawer:React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const {
    isVisible,
    milkTestData,
    handleHiddenDrawer,
    handleSubmitMilkTest,
    inhibitorOptions,
    initialDate
  } = props

  const { systemData: { languages, logged: { languageId } } } = useAuth()

  const languageLocal = languages.find(language => language.id === languageId)

  const { control, reset, handleSubmit, formState: { isSubmitted }, clearErrors, setValue } = useForm<MilkTestType>({
    mode: 'onSubmit'
  })

  const watchDate = useWatch({ control, name: 'date' })

  const milkTestFields = [
    { label: t('Cell count (1000/ml)'), key: 'cellCount',
      type: 'number',
      rules: {
        min: {
          value: 0,
          message: t('Cell count must be 0 or more')
        }
      }
    },
    { label: t('Procurement month'), key: 'date', type: 'date', format: 'MMM YYYY' },
    { label: t('Fat (in %)'), key: 'fat', type: 'number', format: 'percent', suffix: '%',
      rules: {
        required: t('Fat is required'),
        min: {
          value: 1,
          message: t('Fat must be at least 1')
        },
        max: {
          value: 10,
          message: t('Fat must be at most 10')
        }
      }
    },
    { label: t('Fat-free dry matter (in %)'), key: 'fatFreeDryMatter', type: 'number', format: 'percent', suffix: '%',
      rules: {
        min: {
          value: 0,
          message: t('Fat-free dry matter must be 0 or more')
        }
      }
    },
    { label: t('Free fatty acid (in %)'), key: 'freeFattyAcid', type: 'number', format: 'percent', suffix: '%',
      rules: {
        min: {
          value: 0,
          message: t('Free fatty acid must be 0 or more')
        }
      }
    },
    { label: t('Freezing point (in °C)'), key: 'freezingPoint', type: 'number', format: 'percent', suffix: '°C' },
    { label: t('Germ count (1000/ml)'), key: 'germCount', type: 'number',
      rules: {
        min: {
          value: 0,
          message: t('Germ count must be 0 or more')
        }
      }
    },
    { label: t('Inhibitor'), key: 'inhibitor', type: 'select', optionsKey: 'inhibitorValues',
      rules: {
        required: t('Inhibitor is required')
      }
    },
    { label: t('Lactose (in %)'), key: 'lactose', type: 'number', format: 'percent', suffix: '%',
      rules: {
        min: {
          value: 0,
          message: t('Lactose must be at least 0')
        },
        max: {
          value: 10,
          message: t('Lactose must be at most 10')
        }
      }
    },
    { label: t('Protein (in %)'), key: 'protein', type: 'number', format: 'percent', suffix: '%',
      rules: {
        required: t('Protein is required'),
        min: {
          value: 1,
          message: t('Protein must be at least 1')
        },
        max: {
          value: 10,
          message: 'Protein must be at most 10'
        }
      }
    },
    { label: t('Urea (mg/dl)'), key: 'urea', type: 'number', format: 'percent', suffix: 'mg/dl',
      rules: {
        min: {
          value: 0,
          message: t('Urea must be 0 or more')
        }
      }
    }
  ];

  useEffect(() => {
    if(isVisible && milkTestData) {
      reset({
        ...milkTestData,
        date: dayjs(new Date(milkTestData.date)).format('DD.MM.YYYY'),
        inhibitor: milkTestData.inhibitor?.toString()
      })
    }
  }, [isVisible, milkTestData])

  function renderDate(fieldData) {
    const date = milkTestData?.date || initialDate
    const startOfMonth = dayjs(date).startOf('month').format('DD.MM.YYYY');
    const endOfMonth = dayjs(date).endOf('month').format('DD.MM.YYYY');

    return <Controller
      control={control}
      name={fieldData.key}
      rules={{
        required: {
          value: true,
          message: t('Please select date')
        },
        validate: {
          afterOrEqualStartOfMonth: (value: string) =>{
            const valueDate = parseDate(value)
            const startParsed = parseDate(startOfMonth)
            if(dayjs(valueDate).diff(dayjs(startParsed)) < 0) {
              return t('Date must be after or equal to start of month')
            }
            return true
          },
          beforeOrEqualEndOfMonth: (value: string) => {
            const valueDate = parseDate(value)
            const endMonthParsed = parseDate(endOfMonth)
            if(dayjs(endMonthParsed).diff(dayjs(valueDate)) < 0) {
              return t('Date must be before or equal to end of month')
            }
            return true
          }
        }
      }}
      render={({ field, fieldState }) => {
        return <Form.Field
          required
          error={!watchDate && isSubmitted ? getError(fieldState) : false}
        >
          <label>{fieldData.label}</label>
          <DateInput
            name='dateTime'
            placeholder={fieldData.label}
            minDate={startOfMonth}
            maxDate={endOfMonth}
            initialDate={watchDate || startOfMonth}
            closable
            value={watchDate}
            onKeyDown={(event) => event.preventDefault()}
            markColor={'blue'}
            localization={languageLocal.code}
            closeOnMouseLeave={false}
            dateFormat={'DD.MM.YYYY'}
            className={'datePicker'}
            iconPosition='left'
            onChange={(event, { name, value }) => {
              setValue(fieldData.key, value)
              clearErrors('date')
            }}
          />
          {fieldState?.error?.message && <StyledErrorLabel className='ui pointing basic label'>{fieldState.error.message}</StyledErrorLabel>}
        </Form.Field>

      }}
    />
  }

  function renderSelect(field) {
    return <FormFields.Select
      control={control}
      selectOnBlur={false}
      name={field.key}
      label={field.label}
      placeholder={field.label}
      options={inhibitorOptions.map(option => ({ value: option.key.toString(), text: option?.label }))}
      rules={field.rules}
    />
  }

  function getField(field) {
    switch(field.type) {
      case 'number':
        return <FormFields.Input
          control={control}
          typeInput='controled'
          suffix={` ${field.suffix || ''}`}
          decimalScale={2}
          allowDecimals
          name={field.key}
          rules={field.rules}
          key={field.key}
          label={field.label}
          placeholder={field.label}
        />
      case 'date':
        return renderDate(field)
      case 'select':
        return renderSelect(field)
      default:
        return <FormFields.Input
          control={control}
          decimalScale={2}
          allowDecimals
          rules={field.rules}
          typeInput='controled'
          suffix={` ${field.suffix || ''}`}
          name={field.key}
          key={field.key}
          label={field.label}
          placeholder={field.label}
        />
    }
  }

  function renderForm() {
    return <>
      {milkTestFields.map(field => {
        return getField(field)
      })}
      <DrawerFooter>
        <GlobalEl.PrimaryButton
          type='button' onClick={handleSubmit(onSubmit)}
        >{!milkTestData ? t('Add') : t('Update')}</GlobalEl.PrimaryButton>
      </DrawerFooter>
    </>
  }

  function onSubmit(data) {
    milkTestFields.map(milkTestField => {
      if(milkTestField.type === 'number' && data[milkTestField.key]) {
        data[milkTestField.key] = parseInt(data[milkTestField.key]?.toString()?.replace(',', '.'))
      }
    })
    if(milkTestData?.id) {
      handleSubmitMilkTest({ ...data, date: parseDate(data.date) }, milkTestData?.id)
      reset(defaultMilkTestData)
      return
    }
    handleSubmitMilkTest({ ...data, date: parseDate(data.date) })
    reset(defaultMilkTestData)
  }

  return <Drawer
    isVisible={isVisible}
    content={isVisible && renderForm()}
    header={milkTestData ? t('Edit Milk Test {{date}}', { date: dayjs(milkTestData.date).format('DD MMM YYYY') }) : t('Add New Milk Test')}
    onHide={() => {
      reset(defaultMilkTestData)
      handleHiddenDrawer();
    }}
    closeOnEscape
  />
}
export default MilkTestEditDrawer